/**
 * System info & maintenance-mode section (platform admin only).
 *
 * Extracted from AdminSettingsPage.tsx (Sub-PR 6.3).
 */

import { useTranslation } from 'react-i18next'
import { SettingCard } from './SettingCard'
import { Toggle } from './Toggle'
import type { AdminSettings } from './types'

interface SystemSectionProps {
  settings: AdminSettings
  setSettings: (s: AdminSettings) => void
}

export function SystemSection({ settings, setSettings }: SystemSectionProps): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <SettingCard title={t('adminSettings.system')} description={t('adminSettings.systemDesc')}>
      <div className="space-y-4">
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
          <Toggle
            enabled={settings.maintenanceMode}
            onChange={(value) => {
              setSettings({ ...settings, maintenanceMode: value })
            }}
            label={t('adminSettings.maintenanceMode')}
          />
          <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
            {t('adminSettings.maintenanceModeDesc')}
          </p>
        </div>
        <SystemInfo />
      </div>
    </SettingCard>
  )
}

function SystemInfo(): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="border-t border-[var(--border)] pt-4">
      <h4 className="mb-3 text-sm font-medium text-[var(--text-dim)]">
        {t('adminSettings.systemInformation')}
      </h4>
      <dl className="space-y-2 text-sm">
        <div className="flex justify-between">
          <dt className="text-[var(--text-muted)]">{t('adminSettings.apiVersion')}</dt>
          <dd className="font-mono text-[var(--text)]">v1.0.0</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-[var(--text-muted)]">{t('adminSettings.environment')}</dt>
          <dd className="font-mono text-[var(--text)]">
            {import.meta.env.DEV ? 'development' : 'production'}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-[var(--text-muted)]">{t('adminSettings.build')}</dt>
          <dd className="font-mono text-[var(--text)]">
            {new Date().toISOString().split('T')[0]}
          </dd>
        </div>
      </dl>
    </div>
  )
}
